import { ReactElement, useEffect, useRef, useState } from "react";
import { Box, Grid, IconButton, SxProps, Theme, Typography } from "@mui/material";
import { Button } from "../reusable-components";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Progress from "../common/progress";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { operatorAdviceErrorOccurred, operatorAdviceScrollToTop } from "../App";
import useDetailRound from "../rounds/useDetailRound";
import { IsComeOn, themeC } from "../theme/themehelper";
import { ComeOnReceipt } from "../images/comeonicons";

/* ------------------------------------------------------------------ */
/*  Sx styles                                                         */
/* ------------------------------------------------------------------ */

const overlayBackdropSx: SxProps<Theme> = {
  position: "fixed",
  background: "rgba(6,6,6,0.5)",
  top: 0,
  left: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 90,
  height: "100%",
  width: "100%",
};

const clickTrapSx: SxProps<Theme> = {
  position: "absolute",
  width: "100%",
  height: "100%",
};

const confirmWindowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "var(--background_2)",
  borderRadius: "var(--radius)",
  padding: "12px 12px 24px 12px",
  width: 300,
  minHeight: 160,
  position: "absolute",
  top: 250,
  zIndex: 99,
};

const flexEndSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
};

const iconHolderSx: SxProps<Theme> = {
  fontSize: "60px",
  margin: "5px 0px 5px 0px",
};

const messageSx: SxProps<Theme> = {
  fontWeight: "bold",
  fontSize: "14px",
  margin: "20px 0",
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

interface ErrorResponse {
  localizedOperatorMessage?: string;
  errorCode?: number;
  operatorErrorCode?: number;
  success?: boolean;
}

interface Phrases {
  confirmPurchase: string;
  pay: string;
  paymentDone: string;
  insufficientFunds: string;
  gamingLimitsReached: string;
  unableToCompletePurchase: string;
  errorCode: string;
}

function getErrorMessage(errorResponse: ErrorResponse, phrases: Phrases): string {
  if (errorResponse.localizedOperatorMessage !== undefined && errorResponse.localizedOperatorMessage !== "") {
    return errorResponse.localizedOperatorMessage;
  }
  if (errorResponse.errorCode === 101) {
    return phrases.unableToCompletePurchase;
  }
  if (errorResponse.errorCode === 102) {
    return phrases.insufficientFunds;
  }
  if (errorResponse.errorCode === 105) {
    return phrases.gamingLimitsReached;
  }
  return "";
}

/* ------------------------------------------------------------------ */
/*  RenderSwitch                                                      */
/* ------------------------------------------------------------------ */

interface RenderSwitchProps {
  submitting: boolean;
  shouldRenderConfirm: boolean;
  toggle: () => void;
  amount: number;
  currency: string;
  phrases: Phrases;
  handleClick: () => void;
  response: ErrorResponse | undefined;
  t: (key: string) => string;
}

function RenderSwitch({
  submitting,
  shouldRenderConfirm,
  toggle,
  amount,
  currency,
  phrases,
  handleClick,
  response,
  t,
}: RenderSwitchProps): ReactElement {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (response !== undefined && !response.success) {
    const errorMessage = getErrorMessage(response, phrases);
    operatorAdviceErrorOccurred({
      errorCode: response.errorCode,
      errorMessage: errorMessage,
      operatorErrorCode: response.operatorErrorCode,
    });
    return (
      <Box>
        <Typography sx={{ marginTop: "30px", textAlign: "center" }}>
          {phrases.unableToCompletePurchase}, {phrases.errorCode}: {response.errorCode}
        </Typography>
        <Typography sx={{ textAlign: "center" }}>{errorMessage}</Typography>
        <Button width={200} onClick={() => toggle()}>Ok</Button>
      </Box>
    );
  }

  if (submitting) {
    return <Progress isSmallScreen={false} />;
  }

  if (shouldRenderConfirm) {
    return (
      <>
        <Typography sx={messageSx}>{phrases.paymentDone}</Typography>
        <Button width={200} onClick={() => toggle()}>Ok</Button>
      </>
    );
  }

  return (
    <>
      <Typography sx={messageSx}>{phrases.confirmPurchase} {formatStringCurrency(amount, currency, t)}?</Typography>
      <Button type="submit" width={200} onClick={handleClick}>{phrases.pay}</Button>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  ConfirmPaymentApi                                                 */
/* ------------------------------------------------------------------ */

interface ConfirmPaymentApiProps {
  toggle: () => void;
  submitting: boolean;
  response: ErrorResponse | undefined;
  uploadFile: () => void;
  roundId: string;
  count: number | undefined;
}

function ConfirmPaymentApi({
  toggle,
  submitting,
  response,
  uploadFile,
  roundId,
  count,
}: ConfirmPaymentApiProps): ReactElement {
  const [shouldRenderConfirm, setShouldRenderConfirm] = useState(false);
  const { t } = useTranslation();
  const { round } = useDetailRound(roundId);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [iconColor, setIconColor] = useState("black");

  useEffect(() => {
    if (wrapperRef.current) {
      const styles = getComputedStyle(wrapperRef.current);
      const color = styles.getPropertyValue("--text-black").trim();
      if (window.theme === themeC.ComeOn) {
        setIconColor("#C0F9C8");
      } else if (color) {
        setIconColor(color);
      }
    }
  }, []);

  const phrases: Phrases = {
    confirmPurchase: t("do_you_want_to_place_bet"),
    pay: t("pay"),
    paymentDone: t("your_bet_is_paid"),
    insufficientFunds: t("insufficient_funds"),
    gamingLimitsReached: t("gaming_limits_reached"),
    unableToCompletePurchase: t("unable_to_complete_purchase"),
    errorCode: t("error_code"),
  };

  const handleClose = (): void => {
    toggle();
  };

  const handleClick = (): void => {
    uploadFile();
    setShouldRenderConfirm(true);
    operatorAdviceScrollToTop(500);
  };

  let turnoverCurrency = "NA";
  let amount = 0;

  const couponTotal = (round: any, count: number): number => count * round.operatorStakePerRow.amount;

  if (round !== undefined) {
    turnoverCurrency = round.operatorTurnover ? round.operatorTurnover.currency : "NA";
    amount = couponTotal(round, count ?? 0);
  }

  const currency = turnoverCurrency;

  return (
    <Box ref={wrapperRef} sx={overlayBackdropSx}>
      <Box sx={clickTrapSx} onClick={() => {}} />
      <Box sx={confirmWindowSx}>
        <Box sx={flexEndSx}>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ color: iconColor, cursor: "pointer" }} />
          </IconButton>
        </Box>

        <Box sx={iconHolderSx}>
          {IsComeOn(window.theme) ? (
            <Grid>{ComeOnReceipt(51, 50, iconColor)}</Grid>
          ) : (
            <ReceiptOutlinedIcon sx={{ color: iconColor }} fontSize="inherit" />
          )}
        </Box>

        <RenderSwitch
          submitting={submitting}
          shouldRenderConfirm={shouldRenderConfirm}
          toggle={toggle}
          amount={amount}
          currency={currency}
          phrases={phrases}
          handleClick={handleClick}
          response={response}
          t={t}
        />
      </Box>
    </Box>
  );
}

export default ConfirmPaymentApi;
